import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { PaginatorModule } from 'primeng/paginator';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import {
  EMPTY_FORM_ASSIGNMENT_PAGE_MODEL,
  PatientFormAssignmentModel,
  PatientFormAssignmentPageModel,
  PatientFormAssignmentStatus,
} from '../../global-data-models/form-assignment.model';
import {
  DEFAULT_FORM_ASSIGNMENT_PAGE_OPTIONS,
  DEFAULT_PATIENT_FORM_ASSIGNMENT_PAGE_OPTIONS,
  OrderDir,
  FormAssignmentPageOptions,
  PatientFormAssignmentPageOptions,
} from '../../global-services/form-assignment.service';
import { FormAssignmentsFacade, FormAssignmentsUiState, FormAssignmentsViewMode } from './form-assignments.facade';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DownloadService } from '../../global-services/download.service';
import { FormService } from '../../global-services/form.service';
import { FormResponseViewerComponent, FormResponseViewerData } from '../../global-components/form-response-viewer/form-response-viewer.component';
import { DialogModule } from 'primeng/dialog';
import { FormModel } from '../../global-data-models/form.model';
import { GlobalMessageService } from '../../global-services/global-message.service';

@Component({
  selector: 'app-form-assignments',
  standalone: true,
  imports: [...COMMON_IMPORTS, PaginatorModule, DropdownModule, TagModule, TooltipModule, AccordionModule, DialogModule, FormResponseViewerComponent],
  templateUrl: './form-assignments.component.html',
  styleUrl: './form-assignments.component.css',
})
export class FormAssignmentsComponent implements OnInit, OnDestroy {
  public readonly OrderDir = OrderDir;
  public readonly PatientFormAssignmentStatus = PatientFormAssignmentStatus;
  @Input() orgId!: string;
  @Input() patientId?: string;
  @Input() collapsible: boolean = false;
  @Input() viewMode!: FormAssignmentsViewMode;

  public accordionActiveIndex: number | null = null;

  public formAssignmentPage: PatientFormAssignmentPageModel = EMPTY_FORM_ASSIGNMENT_PAGE_MODEL;
  public pageOptions: FormAssignmentPageOptions | PatientFormAssignmentPageOptions =
    this.viewMode === 'org' ? DEFAULT_FORM_ASSIGNMENT_PAGE_OPTIONS : DEFAULT_PATIENT_FORM_ASSIGNMENT_PAGE_OPTIONS;

  private uiStateSub?: Subscription;
  private searchSub?: Subscription;
  private statusFilterSub?: Subscription;

  public readonly searchControl = new FormControl('');
  public readonly statusFilterControl = new FormControl('all');

  public readonly statusFilterOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Pending', value: 'ASSIGNED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Expired', value: 'EXPIRED' },
  ];

  public showResponseViewer = false;
  public responseViewerData?: FormResponseViewerData;

  public constructor(
    private readonly facade: FormAssignmentsFacade,
    private readonly router: Router,
    private readonly downloadService: DownloadService,
    private readonly formService: FormService,
    private readonly messageService: GlobalMessageService,
  ) {
    this.setupSearchSubscription();
    this.setupFilterSubscriptions();
  }

  public async ngOnInit() {
    if (!this.orgId) {
      console.error('orgId not provided to FormAssignmentsComponent');
      return;
    }
    if (!this.patientId && this.viewMode === 'patient') {
      console.error('patientId not provided to FormAssignmentsComponent');
      return;
    }

    this.uiStateSub = this.facade.uiState$.subscribe((state: FormAssignmentsUiState) => {
      this.formAssignmentPage = state.formAssignmentPage;
    });

    // Set the required IDs first
    this.updatePageOption('orgId', this.orgId);
    if (this.viewMode === 'patient') {
      this.updatePageOption('patientId', this.patientId!);
    }

    // Apply default filters
    this.applyStatusFilter(this.statusFilterControl.value || 'all');

    // Load data only once after all options are set
    await this.reloadFormAssignments();
  }

  private updatePageOption(key: string, value: any) {
    this.pageOptions = {
      ...this.pageOptions,
      [key]: value,
    };
  }

  private setupSearchSubscription() {
    // Delay subscription to avoid initial trigger
    setTimeout(() => {
      this.searchSub = this.searchControl.valueChanges
        .pipe(
          debounceTime(300), // 300ms debounce
          distinctUntilChanged(),
        )
        .subscribe(async (searchTerm: string | null) => {
          this.updatePageOption('search', searchTerm || '');
          await this.reloadFormAssignments();
        });
    }, 100);
  }

  private setupFilterSubscriptions() {
    // Status filter subscription - only subscribe after initial load
    setTimeout(() => {
      this.statusFilterSub = this.statusFilterControl.valueChanges.pipe(distinctUntilChanged()).subscribe(async (statusFilter: string | null) => {
        this.applyStatusFilter(statusFilter || 'all');
        await this.reloadFormAssignments();
      });
    }, 100);
  }

  private applyStatusFilter(statusFilter: string) {
    if (statusFilter === 'all') {
      this.updatePageOption('status', undefined);
    } else {
      this.updatePageOption('status', statusFilter);
    }
  }

  public async onCardSort(field: string) {
    const newOrder = this.pageOptions.orderBy === field && this.pageOptions.orderDir === OrderDir.ASC ? OrderDir.DESC : OrderDir.ASC;

    this.updatePageOption('orderDir', newOrder);
    this.updatePageOption('orderBy', field);
    await this.reloadFormAssignments();
  }

  public async onPaginatorPageEvent(event: any) {
    this.updatePageOption('limit', event.rows);
    this.updatePageOption('page', (event.first || 0) / (event.rows || 1) + 1);
    await this.reloadFormAssignments();
  }

  public getStatusSeverity(status: PatientFormAssignmentStatus | string | undefined): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
    if (!status) return 'secondary';

    switch (status) {
      case PatientFormAssignmentStatus.COMPLETED:
        return 'success';
      case PatientFormAssignmentStatus.IN_PROGRESS:
        return 'info';
      case 'ASSIGNED' as any:
        return 'warning';
      case PatientFormAssignmentStatus.EXPIRED:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  public getStatusDisplayValue(status: PatientFormAssignmentStatus | string | undefined): string {
    if (!status) return 'Unknown';

    switch (status) {
      case PatientFormAssignmentStatus.COMPLETED:
        return 'Completed';
      case PatientFormAssignmentStatus.IN_PROGRESS:
        return 'In Progress';
      case 'ASSIGNED' as any:
        return 'Pending';
      case PatientFormAssignmentStatus.EXPIRED:
        return 'Expired';
      default:
        return status.toString() || 'Unknown';
    }
  }

  public async reloadFormAssignments(): Promise<void> {
    await this.facade.loadFormAssignments(this.viewMode, this.pageOptions);
  }

  public ngOnDestroy(): void {
    this.uiStateSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.statusFilterSub?.unsubscribe();
    this.facade.destroy();
  }

  public getFormName(assignment: any): string {
    return assignment.form?.name || 'No form assigned';
  }

  public getFormNameTruncated(assignment: any): string {
    const name = assignment.form?.name || 'No form assigned';
    return name.length > 50 ? name.slice(0, 50) + '...' : name;
  }

  public getFormIdentifier(assignment: any): string {
    return assignment.form?.identifier || `Assignment: ${assignment.id?.substring(0, 8)}...`;
  }

  public isStatus(assignment: any, status: string): boolean {
    return assignment.status === status;
  }
  public hasStatusIn(assignment: any, statuses: string[]): boolean {
    return statuses.includes(assignment.status);
  }

  public getLatestFormAssignment(): PatientFormAssignmentModel | null {
    if (!this.formAssignmentPage.data || this.formAssignmentPage.data.length === 0) {
      return null;
    }

    // Return the first assignment since they're typically sorted by creation date descending
    return this.formAssignmentPage.data[0];
  }

  public getFormAssignmentIcon(assignment: PatientFormAssignmentModel): string {
    if (this.isStatus(assignment, 'COMPLETED')) {
      return 'pi-file-edit';
    } else if (this.isStatus(assignment, 'IN_PROGRESS')) {
      return 'pi-file';
    } else if (this.hasStatusIn(assignment, ['PENDING', 'ASSIGNED'])) {
      return 'pi-file-o';
    } else if (this.hasStatusIn(assignment, ['EXPIRED', 'CANCELLED'])) {
      return 'pi-times-circle';
    }
    return 'pi-file';
  }

  public getFormAssignmentIconColor(assignment: PatientFormAssignmentModel): string {
    if (this.isStatus(assignment, 'COMPLETED')) {
      return 'text-green-600';
    } else if (this.isStatus(assignment, 'IN_PROGRESS')) {
      return 'text-blue-600';
    } else if (this.hasStatusIn(assignment, ['PENDING', 'ASSIGNED'])) {
      return 'text-orange-600';
    } else if (this.hasStatusIn(assignment, ['EXPIRED', 'CANCELLED'])) {
      return 'text-red-600';
    }
    return 'text-gray-600';
  }

  public onFormCardClick(assignment: PatientFormAssignmentModel, patientId?: string): void {
    if (!assignment.id) {
      console.error('Assignment ID not available for navigation');
      return;
    }

    const url = `/orgs/${this.orgId}/patients/${assignment.patientId}/form-assignments/${assignment.id}/view`;
    this.router.navigate([url]);
  }

  public async onDownloadCsv(assignment: PatientFormAssignmentModel): Promise<void> {
    if (!assignment.responseData) return;
    const filename = `form-responses-${assignment.id || 'unknown'}.csv`;
    try {
      this.downloadService.downloadAsCsv({ data: assignment.responseData, filename });
    } catch (error: any) {
      this.messageService.showError('Download Error', error?.message || 'Failed to download CSV');
    }
  }

  public async onViewResponse(assignment: PatientFormAssignmentModel): Promise<void> {
    if (!assignment.formId || !assignment.responseData) return;
    try {
      const form: FormModel = await this.formService.getForm(this.orgId, assignment.formId);
      this.responseViewerData = {
        formDefinition: form.formData,
        responseData: assignment.responseData,
        formName: `${form.name} [${form.identifier} V${form.version}]`,
        assignmentId: assignment.id,
        patientId: assignment.patient?.id,
        patientName: assignment.patient ? `${assignment.patient.firstName} ${assignment.patient.lastName}` : undefined,
        formId: form.id,
        formIdentifier: form.identifier,
        formVersion: form.version,
        completedAt: assignment.completedAt,
      };
      this.showResponseViewer = true;
    } catch (error: any) {
      this.messageService.showError('View Response Error', error?.message || 'Failed to load form definition for response viewer');
    }
  }

  public onCloseResponseViewer(): void {
    this.showResponseViewer = false;
    this.responseViewerData = undefined;
  }
}
